/** @jsx jsx */
import React from "react"
import { jsx } from "@emotion/react"
import { Link } from "gatsby"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Headroom from "react-headroom"

import { colorScheme, fontScheme } from "../../helpers/styleSettings"

const Header = styled.header`
  width: 100%;
  height: 58px;
  z-index: 901;
  display: flex;
  align-items: center;
  margin: 0 auto;
  padding: 0 1em;
  transition: transform 0.4s ease;
  transform: translateY(0);

  @media (max-width: 768px) {
    padding: 0 2em;
    height: 28vw;
    display: flex;
    flex-wrap: wrap;
    background-color: #fff;
  }
`

const headroomStyles = css`
  width: 100vw;

  .headroom {
    transition: all 0.3s ease;
    background: transparent;
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to top,
        transparent 0,
        rgba(0, 0, 0, 0.5) 100%
      );
      z-index: -1;
      transition: all 0.3s ease;
      opacity: 0;
    }
    &--unpinned {
      transform: translateY(-100%);
      background: transparent;
      &::before {
        opacity: 0;
      }
    }

    &--scrolled {
      &.headroom--pinned {
        background: ${colorScheme.supportLightGrey};
        &::before {
          opacity: 0;
        }
      }

      a {
        color: ${colorScheme.text};
      }
    }
  }
`

const SiteNav = styled.nav`
  font-family: ${fontScheme.headings};
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-size: 1.125em;
  margin-left: auto;
  margin-right: 58px;
  & ul {
    list-style: none;
    & li {
      display: inline;
      margin-left: 20px;
    }
    li:first-of-type {
      a {
        color: ${colorScheme.supportDark};
      }
      @media (max-width: 768px) {
        display: none; // Remove the email in navigation on mobile
      }
    }
  }
  @media (max-width: 768px) {
    display: flex;
    flex: 1 0 100%;
    width: 100%;
    & ul {
      display: block;
      flex-direction: row;
      padding: 0;
      white-space: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: -ms-autohiding-scrollbar; }
      & li {
        display: inline;
        text-align: center;
      }
    }
  }
  a {
    text-decoration: none;
    flex: 1;
    line-height: 1.2;
    text-decoration: none;
    position: relative;
    overflow: hidden;
    &:hover {
      background-size: 100% 100%;
      color: ${colorScheme.supportGreen};
    }
    &:before {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: -150%;
      height: 2px;
      background-image: linear-gradient(
        to right,
        ${colorScheme.supportPink} 0,
        ${colorScheme.supportGreen} 100%
      );
      background-position: 0 1.2em;
      background-size: 0 100%;
      background-repeat: no-repeat;
      transition: background 0.5s;
    }
  }
`

const Logo = styled(Link)`
  width: 298px;
  display: block;
  height: auto;
  @media (max-width: 768px) {
    width: 220px;
    display: flex;
    margin-top: 5vw;
    justify-content: center;
    flex: 1 0 100%;
  }
  & svg {
    display: block;
    width: 100%;
  }
  & svg:hover {
    fill: ${colorScheme.logo};
  }
`

const navItem = css`
  text-decoration: none;
  color: ${colorScheme.text};
  transition: color 0.3s ease-out;

  &:hover {
    color: ${colorScheme.support};
    background-size: 100% 100%;
  }
`

const SiteHeader = (): JSX.Element => (
  <Headroom
    pinStart={0}
    wrapperStyle={{ position: "fixed", zIndex: 502 }}
    css={headroomStyles}
  >
    <Header>
      <Logo to="/" title="A bit ago">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="165"
          height="37"
          viewBox="0 0 165 37"
        >
          <g fill="none" fillRule="evenodd" transform="translate(0 -7)">
            <path
              fill="#000000"
              fillRule="nonzero"
              d="M22.986,35.36 C23.454,35.36 24.096,35.156 24.912,34.748 C26.304,34.052 27,33.272 27,32.408 C27,32 26.796,31.388 26.388,30.572 L26.388,30.572 L16.668,10.484 C16.044,9.236 15.084,8.612 13.788,8.612 C13.188,8.612 12.618,8.78 12.078,9.116 C11.538,9.452 11.136,9.896 10.872,10.448 L10.872,10.448 L1.152,30.572 C0.744,31.388 0.54,32 0.54,32.408 C0.54,33.272 1.236,34.04 2.628,34.712 C3.444,35.12 4.086,35.324 4.554,35.324 C5.022,35.324 5.406,35.21 5.706,34.982 C6.006,34.754 6.222,34.532 6.354,34.316 C6.486,34.1 6.672,33.752 6.912,33.272 L6.912,33.272 L8.784,29.384 L18.756,29.384 L20.628,33.272 C20.868,33.752 21.054,34.112 21.186,34.352 C21.318,34.592 21.534,34.82 21.834,35.036 C22.134,35.252 22.518,35.36 22.986,35.36 Z M16.164,24.056 L11.376,24.056 L13.752,19.124 L16.164,24.056 Z M41.22,34.964 C43.092,34.964 44.124,34.376 44.316,33.2 C45.18,34.352 46.632,34.928 48.672,34.928 C50.712,34.928 52.62,33.986 54.396,32.102 C56.172,30.218 57.06,28.022 57.06,25.514 C57.06,23.006 56.166,20.804 54.378,18.908 C52.59,17.012 50.628,16.064 48.492,16.064 C46.716,16.064 45.348,16.652 44.388,17.828 L44.388,17.828 L44.388,10.34 C44.388,9.788 44.37,9.374 44.334,9.098 C44.298,8.822 44.184,8.504 43.992,8.144 C43.68,7.496 42.768,7.172 41.256,7.172 C39.72,7.172 38.784,7.496 38.448,8.144 C38.28,8.48 38.178,8.786 38.142,9.062 C38.106,9.338 38.088,9.752 38.088,10.304 L38.088,10.304 L38.088,31.796 C38.088,32.348 38.106,32.762 38.142,33.038 C38.178,33.314 38.28,33.632 38.448,33.992 C38.784,34.64 39.708,34.964 41.22,34.964 Z M47.556,28.664 C46.668,28.664 45.924,28.34 45.324,27.692 C44.748,27.044 44.46,26.33 44.46,25.55 C44.46,24.77 44.742,24.044 45.306,23.372 C45.87,22.7 46.632,22.364 47.592,22.364 C48.552,22.364 49.32,22.694 49.896,23.354 C50.472,24.014 50.76,24.74 50.76,25.532 C50.76,26.324 50.448,27.044 49.824,27.692 C49.2,28.34 48.444,28.664 47.556,28.664 Z M87.66,35 C88.188,35 88.59,34.982 88.866,34.946 C89.142,34.91 89.46,34.808 89.82,34.64 C90.492,34.304 90.828,33.38 90.828,31.868 C90.828,30.572 90.552,29.768 90,29.456 C89.664,29.264 89.346,29.15 89.046,29.114 C88.746,29.078 88.314,29.06 87.75,29.06 C87.186,29.06 86.802,28.916 86.598,28.628 C86.394,28.34 86.292,27.872 86.292,27.224 L86.292,27.224 L86.292,22.256 L88.632,22.4 C89.184,22.4 89.598,22.382 89.874,22.346 C90.15,22.31 90.468,22.208 90.828,22.04 C91.476,21.704 91.8,20.78 91.8,19.268 C91.8,17.732 91.488,16.808 90.864,16.496 C90.504,16.304 90.186,16.19 89.91,16.154 C89.634,16.118 89.226,16.1 88.686,16.1 C88.146,16.1 87.348,16.148 86.292,16.244 L86.292,16.244 L86.292,12.716 C86.292,11.876 86.232,11.264 86.112,10.88 C85.8,9.992 84.828,9.548 83.196,9.548 C81.66,9.548 80.736,9.848 80.424,10.448 C80.232,10.76 80.118,11.048 80.082,11.312 C80.046,11.576 80.028,11.984 80.028,12.536 L80.028,12.536 L80.028,16.244 L78.84,16.1 C78.264,16.1 77.784,16.256 77.4,16.568 C77.184,16.76 77.016,17.078 76.896,17.522 C76.776,17.966 76.716,18.68 76.716,19.664 C76.716,20.648 76.89,21.344 77.238,21.752 C77.586,22.16 78.024,22.364 78.552,22.364 C79.08,22.364 79.572,22.328 80.028,22.256 L80.028,22.256 L80.028,27.584 C80.028,30.272 80.676,32.18 81.972,33.308 C83.268,34.436 85.164,35 87.66,35 Z M118.638,34.928 C119.946,34.928 120.768,34.616 121.104,33.992 C121.272,33.632 121.374,33.314 121.41,33.038 C121.446,32.762 121.464,32.348 121.464,31.796 L121.464,31.796 L121.464,19.196 C121.464,18.644 121.446,18.23 121.41,17.954 C121.374,17.678 121.266,17.36 121.086,17 C120.906,16.64 120.588,16.388 120.132,16.244 C119.676,16.1 118.98,16.028 118.044,16.028 C116.292,16.028 115.344,16.7 115.2,18.044 C115.032,17.804 114.816,17.558 114.552,17.306 C114.288,17.054 113.82,16.784 113.148,16.496 C112.476,16.208 111.732,16.064 110.916,16.064 C108.78,16.064 106.818,17.006 105.03,18.89 C103.242,20.774 102.348,22.97 102.348,25.478 C102.348,27.986 103.236,30.188 105.012,32.084 C106.788,33.98 108.714,34.928 110.79,34.928 C112.866,34.928 114.348,34.292 115.236,33.02 C115.308,33.644 115.566,34.118 116.01,34.442 C116.454,34.766 117.33,34.928 118.638,34.928 Z M111.852,28.628 C110.964,28.628 110.208,28.31 109.584,27.674 C108.96,27.038 108.648,26.324 108.648,25.532 C108.648,24.74 108.942,24.008 109.53,23.336 C110.118,22.664 110.886,22.328 111.834,22.328 C112.782,22.328 113.538,22.664 114.102,23.336 C114.666,24.008 114.948,24.734 114.948,25.514 C114.948,26.294 114.654,27.008 114.066,27.656 C113.478,28.304 112.74,28.628 111.852,28.628 Z M133.164,43.172 C135.564,43.172 137.724,42.326 139.644,40.634 C141.564,38.942 142.524,36.716 142.524,33.956 L142.524,33.956 L142.524,19.052 C142.524,18.5 142.506,18.092 142.47,17.828 C142.434,17.564 142.326,17.258 142.146,16.91 C141.966,16.562 141.66,16.316 141.228,16.172 C140.796,16.028 140.112,15.956 139.176,15.956 C138.24,15.956 137.562,16.13 137.142,16.478 C136.722,16.826 136.476,17.336 136.404,18.008 C136.26,17.792 136.062,17.558 135.81,17.306 C135.558,17.054 135.096,16.778 134.424,16.478 C133.752,16.178 133.008,16.028 132.192,16.028 C130.104,16.028 128.184,16.952 126.432,18.8 C124.68,20.648 123.804,22.796 123.804,25.244 C123.804,27.692 124.674,29.834 126.414,31.67 C128.154,33.506 130.02,34.424 132.012,34.424 C134.004,34.424 135.456,33.824 136.368,32.624 L136.368,32.624 L136.368,33.704 C136.368,34.712 136.08,35.576 135.504,36.296 C134.928,37.016 134.184,37.376 133.272,37.376 C131.928,37.376 130.68,36.932 129.528,36.044 C129.168,35.756 128.82,35.612 128.484,35.612 C128.148,35.612 127.854,35.708 127.602,35.9 C127.35,36.092 126.996,36.44 126.54,36.944 C125.82,37.664 125.46,38.492 125.46,39.428 C125.46,40.148 126.288,40.952 127.944,41.84 C129.6,42.728 131.34,43.172 133.164,43.172 Z M133.11,28.304 C132.234,28.304 131.49,27.992 130.878,27.368 C130.266,26.744 129.96,26.048 129.96,25.28 C129.96,24.488 130.248,23.768 130.824,23.12 C131.4,22.472 132.156,22.148 133.092,22.148 C134.028,22.148 134.772,22.478 135.324,23.138 C135.876,23.798 136.152,24.506 136.152,25.262 C136.152,26.018 135.864,26.714 135.288,27.35 C134.712,27.986 133.986,28.304 133.11,28.304 Z M154.764,35.324 C156.348,35.324 157.878,34.958 159.354,34.226 C160.83,33.494 162.084,32.354 163.116,30.806 C164.148,29.258 164.664,27.488 164.664,25.496 C164.664,22.616 163.668,20.246 161.676,18.386 C159.684,16.526 157.392,15.596 154.8,15.596 C152.208,15.596 149.904,16.532 147.888,18.404 C145.872,20.276 144.864,22.652 144.864,25.532 C144.864,27.452 145.374,29.174 146.394,30.698 C147.414,32.222 148.668,33.374 150.156,34.154 C151.644,34.934 153.18,35.324 154.764,35.324 Z M154.728,28.988 C153.936,28.988 153.156,28.7 152.388,28.124 C151.62,27.524 151.236,26.642 151.236,25.478 C151.236,24.314 151.608,23.432 152.352,22.832 C153.096,22.232 153.9,21.932 154.764,21.932 C155.628,21.932 156.432,22.22 157.176,22.796 C157.92,23.372 158.292,24.248 158.292,25.424 C158.292,26.6 157.896,27.488 157.104,28.088 C156.312,28.688 155.52,28.988 154.728,28.988 Z"
            />
            <ellipse cx="65.5" cy="12" fill="#F768A1" rx="3.5" ry="3" />
            <rect width="9" height="6" x="62" y="29" fill="#7ED321" rx="3" />
            <rect width="9" height="6" x="62" y="19" fill="#F8E71C" rx="3" />
          </g>
        </svg>
      </Logo>
      <SiteNav>
        <ul>
          <li>
            <a css={navItem} href="mailto:studio@abitago.se">
              studio@abitago.se
            </a>
          </li>
          <li>
            <Link css={navItem} to={"/our-story"}>
              Our story
            </Link>
          </li>
          <li>
            <Link css={navItem} to={"/team"}>
              Team
            </Link>
          </li>
          <li>
            <Link css={navItem} to={"/games"}>
              Games
            </Link>
          </li>
          <li>
            <Link css={navItem} to={"/blog"}>
              Blog
            </Link>
          </li>
          <li>
            <Link css={navItem} to={"/career"}>
              Career
            </Link>
          </li>
        </ul>
      </SiteNav>
    </Header>
  </Headroom>
)

export default SiteHeader
